<template>
  <base-modal size="5xl" ref="modal">
    <base-card shadow no-padding>
      <div class="tw-flex tw-justify-between tw-items-center tw-bg-gray-100 tw-px-4 tw-py-3 tw-rounded-t-md">
        <h3 class="tw-text-lg tw-font-semibold">Last Submitted Orders</h3>
        <base-icon class="tw-mr-6 tw-my-auto sm:tw-mr-1.5 sm:tw-mt-1.5" @click="hide()"><x-icon class="tw-h-8 tw-w-8 tw-rounded-full"/></base-icon>
      </div>

      <div class="tw-overflow-auto tw-p-4 max-h-96">
        <base-table ref="orders" :loading="isLoading" :fields="fields" :items="orders" :overflowVisible="false">
          <!-- Activity -->
          <template #activity="{ item: order }">
            <div class="tw-flex">
              <div>
                <div class="tw-flex tw-flex-col tw-items-start">
                  <div class="tw-flex">
                    <div class="tw-flex tw-font-normal tw-text-gray-700 tw-text-base tw-whitespace-nowrap">{{ order.todoItem.type.label }}</div>
                    <base-badge v-if="order.todoItem.isDeleted" class="tw-ml-2" size="sm" color="red">Archived</base-badge>
                  </div>
                  <div class="tw-flex tw-font-normal tw-text-xs tw-whitespace-nowrap">
                    <span class="tw-text-sm tw-text-primary-500">{{ order.todoItem.user.fullName }}</span>
                  </div>
                  <div v-if="order.todoItem.businessLocation.name" class="tw-flex tw-text-xs tw-text-gray-500 tw-whitespace-nowrap">
                    {{ order.todoItem.businessLocation.name }}
                  </div>
                  <span v-else class="tw-text-xs tw-text-red-500">No Location Associated</span>
                  <div v-if="order.todoItem.businessPrescription.programName" class="tw-flex tw-text-xs tw-text-gray-500 tw-whitespace-nowrap">
                    {{ order.todoItem.businessPrescription.programName }}
                  </div>
                  <div v-if="order.todoItem.userBusinessPlan.name" class="tw-flex tw-text-xs tw-text-gray-500 tw-whitespace-nowrap">
                    {{ order.todoItem.userBusinessPlan.name }}
                  </div>
                </div>
              </div>
            </div>
          </template>

          <!-- Pharmacy -->
          <template #pharmacy="{ item: order }">
            <div class="tw-flex tw-items-center tw-text-sm tw-whitespace-nowrap">
              <div :class="'tw-text-gray-500'">{{ order.pharmacy || "N/A" }}</div>
            </div>
          </template>

          <!-- Shipping Address -->
          <template #shippingAddress="{ item: order }">
            <div class="tw-flex tw-items-center tw-text-sm tw-whitespace-nowrap">
              <div :class="'tw-text-gray-500'">{{ order.shippingAddress || "N/A" }}</div>
            </div>
          </template>

          <!-- Expedite -->
          <template #expedite="{ item: order }">
            <div class="tw-flex tw-items-center tw-text-sm tw-whitespace-nowrap">
              <div :class="'tw-text-gray-500'">{{ order.isExpedite ? "Yes" : "No" }}</div>
            </div>
          </template>

          <!-- Inserted Date -->
          <template #insertedDate="{ item: order }">
            <div class="tw-flex tw-items-center tw-text-sm tw-whitespace-nowrap">
              <div :class="'tw-text-gray-500'">{{ formatDate(order.insertedDate) }}</div>
            </div>
          </template>

          <!-- Inserted By -->
          <template #insertedBy="{ item: order }">
            <div class="tw-flex tw-items-center tw-text-sm tw-whitespace-nowrap">
              <div :class="'tw-text-gray-500'">{{ order && order.insertedByUser && order.insertedByUser.fullName ? order.insertedByUser.fullName : "N/A" }}</div>
            </div>
          </template>

          <!-- Empty State -->
          <template #empty-state>
            <div class="tw-flex tw-justify-center tw-items-center tw-py-5 tw-text-sm">
              <span v-if="error" class="tw-text-red-500">{{ error }}</span>
              <span v-else class="tw-text-gray-500">No Results</span>
            </div>
          </template>

          <!-- Loading Placeholder -->
          <template #loading-row>
            <td class="tw-py-3 tw-px-4"><base-skeleton-loader class="tw-h-6 tw-w-12" /></td>
            <td class="tw-py-3 tw-px-4"><base-skeleton-loader class="tw-h-6 tw-w-12" /></td>
            <td class="tw-py-3 tw-px-4"><base-skeleton-loader class="tw-h-6 tw-w-12" /></td>
            <td class="tw-py-3 tw-px-4"><base-skeleton-loader class="tw-h-6 tw-w-12" /></td>
            <td class="tw-py-3 tw-px-4"><base-skeleton-loader class="tw-h-6 tw-w-12" /></td>
            <td class="tw-py-3 tw-px-4"><base-skeleton-loader class="tw-h-6 tw-w-12" /></td>
            <td class="tw-py-3 tw-px-4"><base-skeleton-loader class="tw-h-6 tw-w-12" /></td>
          </template>
        </base-table>
      </div>

      <div class="tw-p-4 tw-flex tw-justify-end">
        <base-button type="primary" @click="hide()">Close</base-button>
      </div>
    </base-card>
  </base-modal>
</template>

<script>
import moment from "moment";
import { XIcon } from "@/assets/svgs";

export default {
  name: "LastSubmittedOrderModal",
  components: { XIcon },
  props: {
    businessId: String,
    orders: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: [String, Boolean],
      default: null,
    },
  },
  computed: {
    fields() {
      return [
        { title: "Activity", key: "activity", thClass: "tw-w-0 tw-text-left" },
        { title: "Pharmacy", key: "pharmacy", thClass: "tw-w-0 tw-text-left tw-whitespace-nowrap" },
        { title: "Shipping Address", key: "shippingAddress", thClass: "tw-w-0 tw-text-left" },
        { title: "Expedite", key: "expedite", thClass: "tw-w-0 tw-text-left tw-whitespace-nowrap" },
        { title: "Inserted Date", key: "insertedDate", thClass: "tw-w-0 tw-text-left tw-whitespace-nowrap" },
        { title: "Inserted By", key: "insertedBy", thClass: "tw-w-0 tw-text-left tw-whitespace-nowrap" },
      ];
    },
    hasData() {
      return Array.isArray(this.orders) && this.orders.length > 0 && !this.error;
    },
  },
  methods: {
    show() {
      this.$refs.modal.show();
    },
    hide() {
      this.$refs.modal.hide();
    },
    formatDate(date) {
      return date ? moment(date).format("M/D/YYYY") : "-";
    },
  },
};
</script>
